import { useCallback, useMemo, useState } from 'react'

import { styled } from 'styled-components'

import type { CheckboxTreeBranchNode } from '@b-stock/fe-core/bstock-react'
import {
  CheckboxTreeNodeType,
  CheckboxTree,
  FormattedMessage,
  Modal,
  getSubcategoryHierarchy,
  AnalyticsEntityType,
  AnalyticsSource,
} from '@b-stock/fe-core/bstock-react'
import { ListingEnums } from '@b-stock/fe-core/locale'
import {
  ItemCategoriesDtoCategoriesEnum,
  ItemCategoriesDtoSubcategoriesEnum,
} from '@b-stock/listing-api-client'

import {
  ModalContent as BaseModalContent,
  ModalFooter,
} from '@/components/common/modal/ui'
import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import type { ThresholdStep } from '@/components/listings/search/ListingSearchProvider/categoryThreshold'
import FilterCheckbox from '@/components/listings/search/SearchFilters/shared/FilterCheckbox'
import {
  FilterModalHeader,
  ModalStatistic,
  TotalSelected,
} from '@/components/listings/search/SearchFilters/shared/Modal'
import {
  ModalPanelArea,
  ModalSectionHeader,
  ModalSummaryFooter,
} from '@/components/listings/search/SearchFilters/shared/SectionedFilterModal'
import { useIsMobileTableLayout } from '@/components/listings/shared/useIsMobileTableLayout'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { createCategoricalAppliedComponent } from '../../shared/filterUtils'
import SeeMoreButton from '../../shared/SeeMoreButton'
import toDisplayNames from '../../toDisplayNames'
import type { ListingFilter, FilterProps } from '../../types'
import {
  CategoryThresholdControl,
  LotCompositionPanel,
  useStagedCategoryThreshold,
  useTrackCategoryFilterApplied,
} from './CategoryThresholdControl'

const ModalContent = styled(BaseModalContent)`
  width: 43.75rem;
`

const MAX_NODES_IN_SIDEBAR = 6

// Unfortunately, the data in the search service isn't an exact match
// to what fe-core says the localizations should be for subcategories,
// so we normalize the strings to support fuzzy matching
const normalize = (str: string): string => {
  return str.replace(/[,&]/g, '').replace(/\s+/g, ' ').trim()
}

const addOldApplianceSubcategories = (
  parentCategoryLookup: Map<string, ItemCategoriesDtoCategoriesEnum>,
  formatSubcategory: (subcategory: ItemCategoriesDtoSubcategoriesEnum) => string
) => {
  // Backwards compatibility for appliance categories used prior to GLOB-3113
  parentCategoryLookup.set(
    normalize(
      formatSubcategory(ItemCategoriesDtoSubcategoriesEnum.MixedAppliances)
    ),
    ItemCategoriesDtoCategoriesEnum.Appliances
  )
  parentCategoryLookup.set(
    normalize(
      formatSubcategory(
        ItemCategoriesDtoSubcategoriesEnum.MajorAppliancesKitchen
      )
    ),
    ItemCategoriesDtoCategoriesEnum.MajorAppliances
  )
  parentCategoryLookup.set(
    normalize(
      formatSubcategory(
        ItemCategoriesDtoSubcategoriesEnum.MajorAppliancesLaundry
      )
    ),
    ItemCategoriesDtoCategoriesEnum.MajorAppliances
  )
  parentCategoryLookup.set(
    normalize(
      formatSubcategory(ItemCategoriesDtoSubcategoriesEnum.MajorAppliancesMixed)
    ),
    ItemCategoriesDtoCategoriesEnum.MajorAppliances
  )
  parentCategoryLookup.set(
    normalize(
      formatSubcategory(ItemCategoriesDtoSubcategoriesEnum.SmallAppliancesMixed)
    ),
    ItemCategoriesDtoCategoriesEnum.SmallAppliances
  )
  parentCategoryLookup.set(
    normalize(
      formatSubcategory(ItemCategoriesDtoSubcategoriesEnum.OutdoorAppliances)
    ),
    ItemCategoriesDtoCategoriesEnum.HomeAndGarden
  )
}

/**
 * CategoryTreeFilter - Displays a hierarchical checkbox tree of categories and subcategories
 *
 * This component filters a predefined category hierarchy to show only categories/subcategories
 * that have actual search results. It handles the complexity of mapping between API values
 * and formatted display names while maintaining proper parent-child relationships.
 *
 * EXAMPLE INPUT: items = [{value: "Heating Cooling & Air Quality", count: 12}]
 * EXAMPLE OUTPUT: Tree with "Appliances" branch and "Heating Cooling & Air Quality" leaves
 */
const CategoryTreeFilter: ListingFilter<Set<string>> = ({
  onChange: propsOnChange,
  value: propsValue,
  desktopScreen,
  items,
}: FilterProps<Set<string>>) => {
  const { trackButtonClicked } = useAnalytics()
  const isMobileFiltersShown = useIsMobileTableLayout()
  const device = isMobileFiltersShown ? 'Mobile' : 'Desktop'

  const formatSubcategory = ListingEnums.Subcategory.useFormat()
  const formatCategory = ListingEnums.Category.useFormat()

  const lookupParentCategory = useMemo(() => {
    const subcategoryHierarchy = getSubcategoryHierarchy()
    const parentCategoryLookup = new Map<
      string,
      ItemCategoriesDtoCategoriesEnum
    >()
    addOldApplianceSubcategories(parentCategoryLookup, formatSubcategory)

    Object.keys(subcategoryHierarchy).forEach((categoryKey) => {
      const typedCategoryKey = categoryKey as keyof typeof subcategoryHierarchy
      const subcategories = subcategoryHierarchy[typedCategoryKey]
      for (const subcategory of subcategories) {
        parentCategoryLookup.set(
          normalize(formatSubcategory(subcategory)),
          typedCategoryKey
        )
      }
    })

    return (itemValue: string) => {
      return (
        parentCategoryLookup.get(normalize(itemValue)) ||
        ItemCategoriesDtoCategoriesEnum.Other
      )
    }
  }, [formatSubcategory])

  const { state } = useListingSearch()
  const trackCategoryFilterApplied = useTrackCategoryFilterApplied()

  const onChange = useCallback(
    (updatedFilters: Set<string>, stagedThreshold?: ThresholdStep | null) => {
      const previousSet = new Set(propsValue)

      const addedItems = Array.from(updatedFilters).filter(
        (item) => !previousSet.has(item)
      )
      const removedItems = Array.from(previousSet).filter(
        (item) => !updatedFilters.has(item)
      )

      // Track selected items (added)
      if (addedItems.length > 0) {
        const mainCategories = new Set<string>()
        const subCategories = new Set<string>()

        addedItems.forEach((item) => {
          const parentCategory = lookupParentCategory(item)
          mainCategories.add(formatCategory(parentCategory))
          subCategories.add(item)
        })

        trackButtonClicked({
          buttonName: 'filters',
          filter_title: 'Categories',
          filter_name: Array.from(mainCategories).join(', '),
          filter_sub_category: Array.from(subCategories).join(', '),
          is_selected: true,
          device,
          alphabetical_filter: false,
          source: AnalyticsSource.HOME_PORTAL,
          entityType: AnalyticsEntityType.BUYER,
        })
      }

      // Track deselected items (removed)
      if (removedItems.length > 0) {
        const mainCategories = new Set<string>()
        const subCategories = new Set<string>()

        removedItems.forEach((item) => {
          const parentCategory = lookupParentCategory(item)
          mainCategories.add(formatCategory(parentCategory))
          subCategories.add(item)
        })

        trackButtonClicked({
          buttonName: 'filters',
          filter_title: 'Categories',
          filter_name: Array.from(mainCategories).join(', '),
          filter_sub_category: Array.from(subCategories).join(', '),
          is_selected: false,
          device,
          alphabetical_filter: false,
          source: AnalyticsSource.HOME_PORTAL,
          entityType: AnalyticsEntityType.BUYER,
        })
      }

      trackCategoryFilterApplied({
        categories: state.filters.categories,
        subcategories: Array.from(updatedFilters),
        stagedThreshold,
      })

      propsOnChange(updatedFilters)
    },
    [
      propsOnChange,
      trackButtonClicked,
      trackCategoryFilterApplied,
      state.filters.categories,
      formatCategory,
      lookupParentCategory,
      device,
      propsValue,
    ]
  )

  const sortedNodes = useMemo(() => {
    const tree: Partial<Record<ItemCategoriesDtoCategoriesEnum, string[]>> = {}

    if (items) {
      for (const option of items) {
        if (typeof option.value === 'string' && option.count) {
          const parentCategory = lookupParentCategory(option.value)
          let subcategoryOptions = tree[parentCategory]
          if (!subcategoryOptions) {
            subcategoryOptions = []
            tree[parentCategory] = subcategoryOptions
          }
          subcategoryOptions.push(option.value)
        }
      }
    }

    const nodes: CheckboxTreeBranchNode[] = Object.entries(tree)
      .map(([category, subcategories]) => {
        const children = subcategories
          .map((subcategory) => {
            return {
              nodeType: CheckboxTreeNodeType.LEAF,
              key: subcategory,
              displayName: subcategory,
            }
          })
          .sort((a, b) => a.displayName.localeCompare(b.displayName))

        return {
          nodeType: CheckboxTreeNodeType.BRANCH,
          key: category,
          displayName: formatCategory(
            category as ItemCategoriesDtoCategoriesEnum
          ),
          expand: category !== ItemCategoriesDtoCategoriesEnum.MixedLots,
          children,
        }
      })
      .filter((node) => node.children.length > 0)
      .sort((a, b) => a.displayName.localeCompare(b.displayName))

    return nodes
  }, [formatCategory, lookupParentCategory, items])

  const [showModal, setShowModal] = useState<boolean>(false)
  const [priorModalExpandedState, setPriorModalExpandedState] = useState<
    Record<string, boolean>
  >({})
  const enableSeeMore =
    desktopScreen && sortedNodes.length > MAX_NODES_IN_SIDEBAR

  const minimalAvailableNodes = useMemo(
    () =>
      desktopScreen
        ? [...sortedNodes].slice(0, MAX_NODES_IN_SIDEBAR)
        : sortedNodes,
    [desktopScreen, sortedNodes]
  )

  const handleSeeMore = () => {
    setShowModal(true)
  }

  const modalCallbacks = useMemo(
    () => ({
      onClose: (expandedState: Record<string, boolean>) => {
        setPriorModalExpandedState(expandedState)
        setShowModal(false)
      },
      onCancel: (expandedState: Record<string, boolean>) => {
        setPriorModalExpandedState(expandedState)
        setShowModal(false)
      },
      onSubmit: (
        values: Set<string>,
        expandedState: Record<string, boolean>,
        stagedThreshold: ThresholdStep | null
      ) => {
        setPriorModalExpandedState(expandedState)
        onChange(values, stagedThreshold)
        setShowModal(false)
      },
    }),
    [onChange]
  )

  return (
    <>
      <CheckboxTree
        nodes={minimalAvailableNodes}
        values={propsValue}
        onChange={onChange}
      />

      {enableSeeMore && (
        <SeeMoreButton showMore={false} handleShowMore={handleSeeMore} />
      )}

      {/* The filters modal renders a staged panel above the tree instead */}
      {desktopScreen && propsValue.size > 0 && <CategoryThresholdControl />}

      {showModal && (
        <FilterModal
          nodes={sortedNodes}
          values={propsValue}
          {...modalCallbacks}
          priorExpandedState={priorModalExpandedState}
        />
      )}
    </>
  )
}

CategoryTreeFilter.expandedOnInit = true
CategoryTreeFilter.hasCategoryThreshold = true

// We don't use live auction data to limit our displayed subcategory options - we always show everything
CategoryTreeFilter.isAlwaysDisplayed = true

CategoryTreeFilter.getOwnProps = (ctx) => {
  return {
    filterKey: 'category',
    value: new Set(ctx.state.filters.category),
    onChange: (value: Set<string>) =>
      ctx.updateFilters({ category: Array.from(value) }),
    items: ctx.data?.allFilters.category,
  }
}

CategoryTreeFilter.label = 'Filters.category'

CategoryTreeFilter.Applied = createCategoricalAppliedComponent(
  'category',
  CategoryTreeFilter.label,
  toDisplayNames.category
)

export default CategoryTreeFilter

type CategoryTreeFilterModalProps = {
  nodes: CheckboxTreeBranchNode[]
  values: Set<string>
  onClose: (expandedState: Record<string, boolean>) => void
  onCancel: (expandedState: Record<string, boolean>) => void
  onSubmit: (
    values: Set<string>,
    expandedState: Record<string, boolean>,
    stagedThreshold: ThresholdStep | null
  ) => void
  priorExpandedState: Record<string, boolean>
}

function FilterModal({
  nodes,
  values,
  onClose,
  onCancel,
  onSubmit,
  priorExpandedState,
}: CategoryTreeFilterModalProps) {
  const [internalValues, setInternalValues] = useState(values)
  // Staged with the checkboxes: applied on "Show results", discarded when the
  // modal unmounts on Cancel / close.
  const threshold = useStagedCategoryThreshold()
  const [expandedState, setExpandedState] =
    useState<Record<string, boolean>>(priorExpandedState)

  const allLeafKeys = useMemo(
    () => new Set(nodes.flatMap((n) => n.children.map((c) => c.key))),
    [nodes]
  )

  const selectedCount = useMemo(
    () => [...internalValues].filter((k) => allLeafKeys.has(k)).length,
    [internalValues, allLeafKeys]
  )

  const allSelected = selectedCount === allLeafKeys.size && allLeafKeys.size > 0
  const someSelected = selectedCount > 0 && !allSelected

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setInternalValues(new Set())
    } else {
      setInternalValues(allLeafKeys)
    }
  }, [allSelected, allLeafKeys])

  const handleChange = useCallback(
    (value: Set<string>) => {
      setInternalValues(value)
    },
    [setInternalValues]
  )

  const handleNodeExpandedChange = useCallback(
    (key: string, expanded: boolean) => {
      setExpandedState({
        ...expandedState,
        [key]: expanded,
      })
    },
    [expandedState, setExpandedState]
  )

  const shouldNodeBeExpanded = (node: CheckboxTreeBranchNode) => {
    return expandedState[node.key] ?? false
  }

  const handleModalClose = useCallback(() => {
    onClose(expandedState)
  }, [onClose, expandedState])

  const handleCancelClick = useCallback(() => {
    onCancel(expandedState)
  }, [onCancel, expandedState])

  const { handleCommit: commitThreshold, staged: stagedThreshold } = threshold
  const handleSubmitClick = useCallback(() => {
    commitThreshold()
    onSubmit(internalValues, expandedState, stagedThreshold)
  }, [
    commitThreshold,
    onSubmit,
    internalValues,
    expandedState,
    stagedThreshold,
  ])

  const selectAll = (
    <FilterCheckbox
      checked={allSelected ? true : someSelected ? 'PARTIAL' : false}
      label={<FormattedMessage id="Filters.modal.selectAll" />}
      onChange={handleSelectAll}
    />
  )
  const selectedSummary = (
    <FormattedMessage
      id="Filters.modal.selectedCategoryCount"
      values={{ count: selectedCount }}
    />
  )

  return (
    <Modal closeModal={handleModalClose}>
      {threshold.enabled ? (
        <>
          <FilterModalHeader
            title={<FormattedMessage id="Filters.category" />}
            onClose={handleModalClose}
            divider={false}
          />
          <ModalPanelArea>
            <LotCompositionPanel
              variant="modal"
              value={threshold.value}
              onCommit={threshold.handleChange}
              disabled={selectedCount === 0}
            />
          </ModalPanelArea>
          <ModalSectionHeader
            title={<FormattedMessage id="Filters.modal.categoriesTitle" />}
          >
            {selectAll}
          </ModalSectionHeader>
        </>
      ) : (
        <FilterModalHeader
          title={<FormattedMessage id="Filters.category" />}
          onClose={handleModalClose}
        >
          {selectAll}
        </FilterModalHeader>
      )}

      <ModalContent $size="tall">
        <CheckboxTree
          nodes={nodes}
          values={internalValues}
          onChange={handleChange}
          splitToColumns
          onBranchExpandedChange={handleNodeExpandedChange}
          shouldNodeBeExpanded={shouldNodeBeExpanded}
        />
      </ModalContent>

      {threshold.enabled ? (
        <ModalSummaryFooter
          summary={selectedSummary}
          note={
            selectedCount > 0 && (
              <FormattedMessage
                id="Listing.filters.categoryThreshold.summaryNote"
                values={{ threshold: threshold.value }}
              />
            )
          }
          onCancel={handleCancelClick}
          onSubmit={handleSubmitClick}
        />
      ) : (
        <ModalFooter>
          <ModalStatistic>
            <TotalSelected data-testid="selected-count">
              {selectedSummary}
            </TotalSelected>
          </ModalStatistic>
          <Modal.Actions
            alignment="end"
            submitMessage={<FormattedMessage id="Common.showResult" />}
            cancelMessage={<FormattedMessage id="Common.cancel" />}
            onCancel={handleCancelClick}
            onSubmit={handleSubmitClick}
          />
        </ModalFooter>
      )}
    </Modal>
  )
}
